import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Check, Star, Calendar, Award, Zap, Crown } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

import './Premium.css';

const benefits = [
  {
    icon: Zap,
    title: 'Ad-Free Experience',
    description: 'Enjoy RatedBite without any advertisements or sponsored content',
  },
  {
    icon: Calendar,
    title: 'Early Reservation Access',
    description: 'Get 48-hour priority access to popular restaurants and time slots',
  },
  {
    icon: Award,
    title: 'Exclusive Events',
    description: 'Access to members-only dining events, tastings, and chef collaborations',
  },
  {
    icon: Star,
    title: 'Premium Badge',
    description: 'Stand out with a premium badge on your profile and reviews',
  },
];

const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 9.99,
    period: 'month',
    description: 'Perfect for trying premium',
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 89.99,
    period: 'year',
    savings: 'Save $30',
    description: 'Best value for food enthusiasts',
    featured: true,
  },
];

const allPlansFeatures = [
  'Unlimited restaurant ratings',
  'Priority customer support',
  'Advanced search filters',
  'Personalized recommendations',
  'Reservation management',
  'Monthly dining insights',
];

export default function Premium({ user }) {
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [processing, setProcessing] = useState(false);

  const checkPremiumStatus = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        setIsPremium(userData.isPremium || false);
      }
    } catch (error) {
      console.error('Error checking premium status:', error);
      const premiumStatus = localStorage.getItem(`premium_${user?.uid}`) === 'true';
      setIsPremium(premiumStatus);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkPremiumStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        isPremium: true,
        premiumSince: new Date(),
        premiumPlan: selectedPlan
      }).catch(() => {
        localStorage.setItem(`premium_${user.uid}`, 'true');
      });

      setIsPremium(true);
    } catch (error) {
      console.error('Error processing subscription:', error);
      alert('Error processing subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-8 max-w-7xl py-16">
        <div className="text-center">
          <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
          <h1 className="serif text-7xl mb-6">RatedBite Premium</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
            Please <a href="/login" className="underline">login</a> or <a href="/signup" className="underline">sign up</a> to subscribe to Premium.
          </p>
        </div>
      </div>
    );
  }

  if (isPremium) {
    return (
      <div className="container mx-auto px-8 max-w-7xl py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
          <div className="flex items-center justify-center gap-3 mb-6">
            <Crown className="w-8 h-8 text-amber-700" />
            <h1 className="serif text-7xl">You're a Premium Member!</h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-12">
            Thank you for supporting RatedBite. Enjoy all your premium benefits!
          </p>
        </motion.div>

        <div className="grid grid-cols-2 gap-8">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border border-border p-8"
              >
                <div className="w-10 h-10 border border-border flex items-center justify-center mb-4">
                  <IconComponent className="w-5 h-5" />
                </div>
                <h3 className="serif text-2xl mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }



  return (
    <div>
      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1759349117105-467c7f8e1442?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkaW5pbmclMjBleHBlcmllbmNlfGVufDF8fHx8MTc2NDAxNTYxNnww&ixlib=rb-4.1.0&q=80&w=1080"
          alt="Premium Experience"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent"></div>
        <div className="absolute bottom-8 left-8 container mx-auto px-8 max-w-7xl">
          <div className="w-1 h-16 bg-border mb-4"></div>
          <h1 className="serif text-5xl text-foreground mb-2">RatedBite Premium</h1>
          <p className="text-sm text-muted-foreground uppercase tracking-widest">
            Elevate Your Dining Experience
          </p>
        </div>
      </div>

      <div className="container mx-auto px-8 max-w-7xl py-16">
        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-8">
            Premium Benefits
          </h2>
          <div className="grid grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="border border-border p-6"
                >
                  <div className="w-10 h-10 border border-border flex items-center justify-center mb-4">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <h3 className="serif text-2xl mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Pricing Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="border-t border-border pt-16"
        >
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-8">
            Choose Your Plan
          </h2>
          <div className="grid grid-cols-2 gap-8 mb-8">
            {plans.map((plan) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className={`
                  border p-8 relative cursor-pointer transition-colors
                  ${selectedPlan === plan.id
                    ? plan.featured
                      ? 'border-foreground bg-stone-50'
                      : 'border-foreground'
                    : plan.featured
                      ? 'border-foreground/30 bg-stone-50/50'
                      : 'border-border'
                  }
                `}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-8 bg-foreground text-background px-3 py-1 text-xs uppercase tracking-widest">
                    Best Value
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="serif text-3xl mb-1">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground">{plan.description}</p>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="serif text-5xl">${plan.price}</span>
                    <span className="text-sm text-muted-foreground">/ {plan.period}</span>
                  </div>
                  {plan.savings && (
                    <p className="text-sm mt-2 text-foreground">{plan.savings}</p>
                  )}
                </div>
                <button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className={`
                    w-full h-12 text-sm uppercase tracking-widest transition-all
                    ${selectedPlan === plan.id
                      ? plan.featured
                        ? 'bg-foreground text-background hover:bg-foreground/90'
                        : 'border border-foreground text-foreground hover:bg-foreground hover:text-background'
                      : 'border border-border text-muted-foreground'
                    }
                    ${processing ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {processing && selectedPlan === plan.id ? 'Processing...' : 'Upgrade Now'}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Features List */}
          <div className="bg-stone-50 p-8 mb-8">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              All Plans Include
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {allPlansFeatures.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <div className="w-5 h-5 border border-foreground flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Cancel anytime. No hidden fees. 30-day money-back guarantee.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
